import { LEVELS, levelFor } from "./levels";

// Decorative illustrations for the KPI intro dialog, drawn with the same rings
// as the KPI tiles

const Ring = ({
  cx,
  cy,
  r,
  score,
  stroke = 7,
}: {
  cx: number;
  cy: number;
  r: number;
  score: number;
  stroke?: number;
}) => {
  const circumference = 2 * Math.PI * r;
  return (
    <g>
      <circle cx={cx} cy={cy} r={r + stroke} fill="#ffffff" />
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke="#e5e7eb"
        strokeWidth={stroke}
      />
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke={levelFor(score).color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - score / 10)}
        transform={`rotate(-90 ${cx} ${cy})`}
      />
      <text
        x={cx}
        y={cy}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={r * 0.62}
        fontWeight={700}
        fill="#1f2937"
      >
        {score.toFixed(1)}
      </text>
    </g>
  );
};

const Sparkle = ({ x, y, size }: { x: number; y: number; size: number }) => (
  <path
    d={`M${x} ${y - size} Q${x} ${y} ${x + size} ${y} Q${x} ${y} ${x} ${y + size} Q${x} ${y} ${x - size} ${y} Q${x} ${y} ${x} ${y - size}Z`}
    fill="#ffffff"
    opacity={0.9}
  />
);

const Backdrop = () => (
  <>
    <circle cx={40} cy={30} r={46} fill="#d6b7fb" opacity={0.5} />
    <circle cx={300} cy={150} r={60} fill="#be89f7" opacity={0.35} />
    <circle cx={270} cy={20} r={18} fill="#ffffff" opacity={0.5} />
  </>
);

export const RingsIllustration = () => (
  <svg viewBox="0 0 320 170" className="h-full w-full" aria-hidden="true">
    <Backdrop />
    <Ring cx={95} cy={95} r={34} score={8.7} />
    <Ring cx={190} cy={70} r={26} score={5.2} stroke={6} />
    <Ring cx={245} cy={125} r={20} score={2.1} stroke={5} />
    <Sparkle x={150} y={30} size={9} />
    <Sparkle x={45} y={140} size={6} />
    <Sparkle x={285} y={70} size={7} />
  </svg>
);

export const LevelsIllustration = () => (
  <svg viewBox="0 0 320 170" className="h-full w-full" aria-hidden="true">
    <Backdrop />
    {LEVELS.map((level, i) => {
      const score = [9.6, 8, 5.5, 2.5][i];
      const cx = 52 + i * 72;
      return (
        <g key={level.label}>
          <Ring cx={cx} cy={78} r={22} score={score} stroke={6} />
          <rect
            x={cx - 33}
            y={122}
            width={66}
            height={24}
            rx={12}
            fill={level.color}
          />
          <text
            x={cx}
            y={134}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={11}
            fontWeight={700}
            fill={level.textColor}
          >
            {level.label}
          </text>
        </g>
      );
    })}
  </svg>
);

export const ListIllustration = () => (
  <svg viewBox="0 0 320 170" className="h-full w-full" aria-hidden="true">
    <Backdrop />
    <rect x={70} y={22} width={180} height={128} rx={14} fill="#ffffff" />
    {[8.1, 4.6, 6.9].map((score, i) => (
      <g key={score}>
        <Ring cx={98} cy={52 + i * 36} r={11} score={score} stroke={4} />
        <rect
          x={120}
          y={46 + i * 36}
          width={[90, 70, 105][i]}
          height={8}
          rx={4}
          fill="#e8d7fd"
        />
        <rect
          x={120}
          y={58 + i * 36}
          width={[60, 85, 50][i]}
          height={5}
          rx={2.5}
          fill="#f3e9fe"
        />
        <circle cx={232} cy={52 + i * 36} r={6} fill="#f3e9fe" />
        <text
          x={232}
          y={52 + i * 36}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={8}
          fontWeight={700}
          fill="#622599"
        >
          i
        </text>
      </g>
    ))}
    <Sparkle x={275} y={45} size={8} />
  </svg>
);

export const EvolutionIllustration = () => (
  <svg viewBox="0 0 320 170" className="h-full w-full" aria-hidden="true">
    <Backdrop />
    <rect x={40} y={24} width={240} height={126} rx={14} fill="#ffffff" />
    {[0, 1, 2].map((i) => (
      <line
        key={i}
        x1={60}
        x2={260}
        y1={60 + i * 30}
        y2={60 + i * 30}
        stroke="#f3e9fe"
        strokeWidth={2}
      />
    ))}
    <polyline
      points="62,122 102,110 142,114 182,88 222,74 258,52"
      fill="none"
      stroke="#622599"
      strokeWidth={4}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {[
      [62, 122],
      [102, 110],
      [142, 114],
      [182, 88],
      [222, 74],
      [258, 52],
    ].map(([x, y]) => (
      <circle
        key={x}
        cx={x}
        cy={y}
        r={5}
        fill="#ffffff"
        stroke="#622599"
        strokeWidth={3}
      />
    ))}
    <g transform="translate(22 8)">
      <rect width={52} height={48} rx={8} fill="#622599" />
      <rect y={0} width={52} height={14} rx={8} fill="#380b60" />
      <text
        x={26}
        y={33}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={20}
        fontWeight={700}
        fill="#ffffff"
      >
        1°
      </text>
    </g>
  </svg>
);
